import axios from 'axios';
import { clearApiCaches } from './apiCache';
import { safeStorage } from '../utils/safeStorage';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class AuthService {
  constructor() {
    this.logoutListeners = [];
    this.loginListeners = [];
  }

  /**
   * Establish a signed-in session from a token the caller already has.
   *
   * Signing in does not only happen on the login screen. Creating an account at
   * a machine after posting a score returns a token too, and that person is as
   * signed in as anybody else.
   *
   * Writing the two storage keys by hand is not enough, and this exists because
   * doing that looked like it worked: the token was stored, so every later page
   * load was correctly signed in, but the application shell holds its own
   * authentication state and had no way to hear about the change. The result was
   * somebody signed in with no header, no menu and no notification bell until
   * they happened to reload.
   */
  setSession(token, user) {
    clearApiCaches();
    try {
      safeStorage.setItem('token', token);
      safeStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      // A browser refusing storage is not a reason to fail a signup. The session
      // will not survive a reload, but the person is signed in right now.
    }
    this.notifyLogin();
  }

  async login(username, password) {
    const response = await axios.post(`${API_URL}/auth/login`, {
      username,
      password
    });

    if (response.data.token) {
      // Whatever an earlier session left in the service worker cache is not
      // this person's to see.
      clearApiCaches();
      safeStorage.setItem('token', response.data.token);
      safeStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  }

  logout() {
    safeStorage.removeItem('token');
    safeStorage.removeItem('user');
    // Kept API responses outlive the session otherwise, and on a shared device
    // the next person to sign in could be shown them (services/apiCache.js).
    clearApiCaches();
    // Notify all listeners that logout occurred
    this.notifyLogout();
  }

  getToken() {
    return safeStorage.getItem('token');
  }

  getCurrentUser() {
    const userStr = safeStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch (error) {
      // A corrupt saved user should read as signed out, not crash every page.
      return null;
    }
  }

  setCurrentUser(user) {
    safeStorage.setItem('user', JSON.stringify(user));
  }

  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /** Mirror of onLogout, for a session being established rather than ended. */
  onLogin(callback) {
    this.loginListeners.push(callback);
    return () => {
      this.loginListeners = this.loginListeners.filter(listener => listener !== callback);
    };
  }

  notifyLogin() {
    this.loginListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in login listener:', error);
      }
    });
  }

  // Event system for logout notifications
  onLogout(callback) {
    this.logoutListeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.logoutListeners = this.logoutListeners.filter(listener => listener !== callback);
    };
  }

  notifyLogout() {
    this.logoutListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in logout listener:', error);
      }
    });
  }
}

export const authService = new AuthService();
